import type {
  DocValidationResult,
  ValidateSubmissionDocumentRequest,
} from "./docValidationTypes";

function validationSkipped(
  requirementId: string,
  summary: string
): DocValidationResult {
  return {
    verdict: "uncertain",
    summary,
    confidence: 0,
    matchedRequirementId: requirementId,
    skipped: true,
  };
}

function parseValidationBody(
  text: string,
  requirementId: string
): DocValidationResult {
  const trimmed = text.trimStart();
  if (trimmed.startsWith("<") || trimmed.startsWith("<!")) {
    return validationSkipped(
      requirementId,
      "Document check API is not available in this environment — upload will continue without AIS validation."
    );
  }
  try {
    return JSON.parse(text) as DocValidationResult;
  } catch {
    return validationSkipped(
      requirementId,
      "Document check returned an invalid response — upload will continue without AIS validation."
    );
  }
}

export async function validateSubmissionDocument(
  request: ValidateSubmissionDocumentRequest
): Promise<DocValidationResult> {
  let res: Response;
  try {
    res = await fetch("/api/submission/validate-document", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });
  } catch {
    return validationSkipped(
      request.requirementId,
      "Document check is unreachable — upload will continue without AIS validation."
    );
  }

  const text = await res.text();

  if (!res.ok) {
    if (res.status === 404) {
      return validationSkipped(
        request.requirementId,
        "Document check API is not available in this environment — upload will continue without AIS validation."
      );
    }
    const parsed = parseValidationBody(text, request.requirementId);
    if (parsed.skipped) return parsed;
    try {
      const errBody = JSON.parse(text) as { error?: string };
      throw new Error(
        errBody.error ?? `Document validation failed (${res.status})`
      );
    } catch (e) {
      if (e instanceof Error && e.message.startsWith("Document validation")) {
        throw e;
      }
      throw new Error(`Document validation failed (${res.status})`);
    }
  }

  return parseValidationBody(text, request.requirementId);
}

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not read file"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}
